import { red } from '@mui/material/colors';
import React from 'react';
import { OrderType } from '../../../interfaces/delivery-company/orders';
import { get_cod } from '../../../utils/cod';
import {
  displayLocation,
  extractMapsPlaceLink,
} from '../../../utils/locations';
import { displayMoney } from '../../../utils/money';

import CopyLinkButton from '../../common/copy-link-button/copy-link-button';
import { OrderIdWithPopover } from '../../common/order-id-with-popover/order-id-with-popover';

import { GenericList } from '../../common/generic-list/generic-list';
import {
  IconWithText,
  mobileDoubleItemsGrid,
} from '../../common/styles/components';
import { showDeliveryBatchChip } from '../../common/styles/delivery-batch.styles';
import {
  getDeliveryStateColor,
  getDeliveryStateIcon,
  showDeliveryState,
} from '../../common/styles/order-states.style';
import PaidIcon from '@mui/icons-material/Paid';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { Updater } from 'use-immer';
import { SortingByColumnState } from '../../common/generic-table/utils';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import InventoryIcon from '@mui/icons-material/Inventory';
import ChecklistIcon from '@mui/icons-material/Checklist';
import LinkIcon from '@mui/icons-material/Link';
import WarningIcon from '@mui/icons-material/Warning';
import { IOrderDeliveryTask } from '../../../interfaces/delivery-company/tasks';
import StaticGoogleMap from '../../common/static-google-map/static-google-map';
import { Box, Typography } from '@mui/material';
import { Workflow } from '../../../enums/orders';
import { OrderDeliveryTaskState } from '../../../enums/tasks';
import { IN_PROGRESS } from '../driver-delivery-tasks/filter_utils';
import { PENDING } from '../driver-delivery-tasks/filter_utils';
import AddressWithIcon from '../../common/styles/address-with-icon';
import {
  getDeliveryTaskStateColor,
  getDeliveryTaskStateIcon,
} from '../../common/styles/delivery-task.styles';

import OrderNotes from '../../common/order-notes/order-notes';
import { CustomerWithIcon } from '../../common/styles/customer-with-icon';
import { MerchantWithIcon } from '../../common/styles/merchant-with-icon';
import ItemsCount from '../../common/labels-input/items-count/items-count';
import { showWorkflow } from '../../common/styles/workflow-display';

interface ResponsiveOrdersListingProps {
  taskList: IOrderDeliveryTask[];
  selectedTasks: Map<string, IOrderDeliveryTask>;
  updateSelectedTasks: Updater<Map<string, IOrderDeliveryTask>>;
  isLoading: boolean;
  sorting: SortingByColumnState;
  setSorting: React.Dispatch<React.SetStateAction<SortingByColumnState>>;
  onCardClick: (task: IOrderDeliveryTask) => void;
  enableSelection: boolean;
  statusFilter: string;
}

const DriverResponsiveOrdersListing = ({
  taskList,
  selectedTasks,
  updateSelectedTasks,
  isLoading,
  sorting,
  setSorting,
  onCardClick,
  enableSelection,
  statusFilter,
}: ResponsiveOrdersListingProps) => {
  return (
    <GenericList<IOrderDeliveryTask>
      sourceView="driver-delivery-order-listing"
      items={taskList}
      selectionEnabled={enableSelection}
      selectedItems={selectedTasks}
      updateSelectedItems={updateSelectedTasks}
      isLoading={isLoading}
      fullWidth
      columns={[
        {
          header: (
            <>
              <b>
                Order Id <br /> (Ref #)
              </b>
            </>
          ),
          id: 'order_id',
          display_body: (task) => (
            <OrderIdWithPopover order={task.order} />
          ),
        },
      ]}
      getItemUID={(task) => task.id}
      rowConfigs={[
        {
          display_row: (task) =>
            mobileDoubleItemsGrid([
              <b key="id">Order #{task.order.order_id}</b>,
              <>
                {task.order.creation_date &&
                  new Date(task.order.creation_date).toLocaleDateString()}
              </>,
            ]),
        },
        {
          display_row: (task) =>
            mobileDoubleItemsGrid([
              task.order.workflow == Workflow.STANDARD && (
                <IconWithText key="cod" centered>
                  <PaidIcon color="primary" />
                  <b>COD : {displayMoney(get_cod(task.order))}</b>
                </IconWithText>
              ),
              <ItemsCount
                centered
                key="package count"
                label={`# items: ${task.order.number_of_packages}`}
              />,
            ]),
        },
        {
          display_row: (task) =>
            mobileDoubleItemsGrid([
              task.order.workflow == Workflow.STANDARD ? (
                <CustomerWithIcon
                  customer={task.order.customer}
                  showPhone
                  centered
                />
              ) : (
                <MerchantWithIcon
                  merchant={task.order.merchant}
                  phone_number={task.order.merchant_location?.contact_phone}
                  centered
                />
              ),
              showWorkflow(task.order.workflow, task.order.is_exchange, task.order.exchange_for, task.order.return_reason),
            ]),
        },
        {
          display_row: (task) => {
            const mapPath = extractMapsPlaceLink(
              task.order.workflow !== Workflow.STANDARD
                ? task.order.merchant_location
                : task.order.customer_location
            );
            const url =
              task.order.workflow !== Workflow.STANDARD
                ? task.order.merchant_location?.url
                : task.order.customer_location?.url;
            const location =
              task.order.workflow !== Workflow.STANDARD
                ? task.order.merchant_location
                : task.order.customer_location;
            return (
              <StaticGoogleMap
                mapPath={mapPath}
                url={url}
                location={location}
              />
            );
          },
        },
        {
          display_row: (task) => (
            <OrderNotes order={task.order} active_task_notes={task.notes} />
          ),
        },
        {
          display_row: (task) => {
            if (task.order.workflow == Workflow.STANDARD && task.order.customer?.notifications_disabled)
              return <Box mt={1} mb={1} color="red" fontWeight={"bold"}>(*) Notifications are disabled for this customer</Box>
            return <></>
          }
        }
      ]}
      cardIcon={(task) => {
        return task.order.is_critical ? (
          <WarningIcon
            sx={{
              color: red[500],
            }}
          />
        ) : (
          <InventoryIcon color={'primary'} />
        );
      }}
      onCardClick={(task) => onCardClick(task)}
      iconBoxStyles={(task) =>
        task.order.is_critical
          ? {
              padding: '4px',
              boxShadow: `0 0 8px ${red[500]}`,
            }
          : {}
      }
      cardFooter={(task) => {
        return {
          content: (
            <IconWithText centered>
              {getDeliveryTaskStateIcon(task.status)}
              <>{task.status.replaceAll('_', ' ')}</>
            </IconWithText>
          ),
          background: getDeliveryTaskStateColor(task.status),
        };
      }}
    />
  );
};

export default DriverResponsiveOrdersListing;
